import { useMemo, type FC } from "react";

import FirstName from "@/app/components/common/actions/inputs-b/FirstName";
import LastName from "@/app/components/common/actions/inputs-b/LastName";
import EmailInput from "@/app/components/common/actions/inputs-b/EmailInput";
import Mobilefiled from "@/app/components/common/actions/inputs-b/Mobilefiled";
import GxCountryDropdown from "@/app/components/common/actions/inputs-b/GxCountryDropdown";
import DynamicIDProofTag from "@/app/components/common/actions/inputs-b/DynamicIDProofTag-1";
import ToggleSwitch from "@/app/components/common/actions/inputs-b/ToggleSwitch";
import SelectDropdownList from "@/app/components/common/actions/dropdowns/SelectDropdownList";

import type { UserCountry, Module } from "@/app/types/adminMenu";
import type { InviteFormData, InviteIdProof } from "./types";
import { GxLineStepper, type GxLineStepperStep } from "@/app/components/common/actions/Stepper/GxLineStepper";
import GxViewRolePermissions from "@/app/page/default/admin-menu/common/GxViewRolePermissions";
import CountrySupportSelector from "@/app/page/default/admin-menu/invite-users/Countrysupportselector";

interface InviteStepProps {
  data: InviteFormData;
  updateField: (field: keyof InviteFormData, value: any) => void;
  subStep: 0 | 1;
  setSubStep: (step: 0 | 1) => void;
}

const INVITE_SUBSTEPS: GxLineStepperStep[] = [
  { id: "personal", label: "Personal Details" },
  { id: "access", label: "Role & Access" },
];

// dummy — static option lists standing in for the real API-backed lookups
// (countries, ID types, roles, groups) until this demo is wired to data.
const DUMMY_COUNTRIES = ["United Arab Emirates", "India", "United Kingdom", "Singapore"];

const DUMMY_ID_TYPES = [
  { id: 1, label: "Passport" },
  { id: 2, label: "Emirates ID" },
  { id: 3, label: "National ID" },
];

const DUMMY_ROLE_OPTIONS = [
  { value: "1", text: "Preparer" },
  { value: "2", text: "Manager" },
  { value: "3", text: "Owner" },
];

const DUMMY_GROUP_OPTIONS = [
  { value: "10", text: "Finance Group" },
  { value: "11", text: "Operations Group" },
];

const DUMMY_ROLE_MODULES: Module[] = [] as Module[];

const DUMMY_COUNTRY_LIST: UserCountry[] = [
  { smeCountryId: 1, countryName: "United Arab Emirates", checkBox: "N" } as UserCountry,
  { smeCountryId: 2, countryName: "India", checkBox: "N" } as UserCountry,
  { smeCountryId: 3, countryName: "United Kingdom", checkBox: "N" } as UserCountry,
];

const InviteStep: FC<InviteStepProps> = ({ data, updateField, subStep, setSubStep }) => {
  const updateIdProof = (patch: Partial<InviteIdProof>) => {
    updateField("idProof", { ...data.idProof, ...patch });
  };

  // Group selection only applies to Manager / Owner roles — mirrors the
  // manager/owner-only visibility rule from the real admin flow.
  const isGroupFieldVisible = useMemo(
    () => data.selectedRoleName === "Manager" || data.selectedRoleName === "Owner",
    [data.selectedRoleName],
  );

  const handleRoleChange = (value: string) => {
    const match = DUMMY_ROLE_OPTIONS.find((r) => r.value === value);
    updateField("selectedRoleValue", value);
    updateField("selectedRoleName", match?.text ?? "");
    if (!match || (match.text !== "Manager" && match.text !== "Owner")) {
      updateField("selectedGroup", "");
    }
  };

  const handleCountryToggle = (smeCountryId: number) => {
    const next = data.supportedCountryIds.includes(smeCountryId)
      ? data.supportedCountryIds.filter((id) => id !== smeCountryId)
      : [...data.supportedCountryIds, smeCountryId];
    updateField("supportedCountryIds", next);
  };

  return (
    <div className="space-y-6">
      <GxLineStepper steps={INVITE_SUBSTEPS} currentStep={subStep} showStepCounter />

      {subStep === 0 && (
        <>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <FirstName
              label="First Name"
              value={data.firstName}
              onChange={(val: string) => updateField("firstName", val)}
            />
            <LastName
              label="Last Name"
              value={data.lastName}
              onChange={(val: string) => updateField("lastName", val)}
            />
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <GxCountryDropdown
              label="Select Country"
              countries={DUMMY_COUNTRIES}
              value={data.selectedCountry}
              onChange={(val: string) => updateField("selectedCountry", val)}
              onValidate={() => {}}
            />
            <DynamicIDProofTag
              idType={data.idProof.idType}
              idNumber={data.idProof.idNumber}
              onIdTypeChange={(idType: { id: number; label: string } | null) =>
                updateIdProof({ idType })
              }
              onIdNumberChange={(idNumber: string) => updateIdProof({ idNumber })}
              countryDialCode={data.mobileDialCode}
              idTypes={DUMMY_ID_TYPES}
            />
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <EmailInput
              label="Email Address"
              value={data.email}
              onChange={(val: string) => updateField("email", val)}
            />
            <Mobilefiled
              label="Mobile Number"
              value={data.mobileNumber}
              onChange={(val: string) => updateField("mobileNumber", val)}
              countryDialCode={data.mobileDialCode}
              onCountryCodeChange={(dialCode: string) => updateField("mobileDialCode", dialCode)}
              triggerValidation={false}
              disable={false}
            />
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div />
            <div>
              <ToggleSwitch
                checked={data.whatsappEnabled}
                onChange={(checked: boolean) => {
                  updateField("whatsappEnabled", checked);
                  if (checked && !data.whatsappNumber && data.mobileNumber) {
                    updateField("whatsappNumber", data.mobileNumber);
                  }
                }}
                label="Receive important updates on Whatsapp"
                labelPosition="left"
              />

              {data.whatsappEnabled && (
                <Mobilefiled
                  label="Whatsapp Number"
                  value={data.whatsappNumber}
                  onChange={(val: string) => updateField("whatsappNumber", val)}
                  countryDialCode={data.whatsappDialCode}
                  onCountryCodeChange={(dialCode: string) => updateField("whatsappDialCode", dialCode)}
                  triggerValidation={false}
                  disable={false}
                />
              )}
            </div>
          </div>
        </>
      )}

      {subStep === 1 && (
        <>
          <button className="gx-p12-sb text-gray-500 mb-2" onClick={() => setSubStep(0)}>
            ← Back to Personal Details
          </button>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <SelectDropdownList
                placeholder="Select Role"
                data={DUMMY_ROLE_OPTIONS}
                value={data.selectedRoleValue}
                onChange={(e: { value: string }) => handleRoleChange(e.value)}
              />
              <div className="gx-admin-view-permisson">
                <GxViewRolePermissions
                  roleName={data.selectedRoleName}
                  description={
                    <>
                      These are the role permissions set by GrowX Admin. To
                      change any permission or request a new role, please
                      contact GrowX Support:{" "}
                      <a href="mailto:support@growx.global" className="gx-p14-b">
                        support@growx.global
                      </a>
                    </>
                  }
                  selfAuth={false}
                  modules={DUMMY_ROLE_MODULES}
                />
              </div>
            </div>

            {isGroupFieldVisible && (
              <SelectDropdownList
                placeholder="Select Group"
                data={DUMMY_GROUP_OPTIONS}
                value={data.selectedGroup}
                onChange={(e: { value: string }) => updateField("selectedGroup", e.value)}
              />
            )}
          </div>

          <div className="grid grid-cols-1 gap-4">
            <CountrySupportSelector
              key={data.supportedCountryIds.join(",")}
              countryList={DUMMY_COUNTRY_LIST.map((c) => ({
                ...c,
                checkBox: data.supportedCountryIds.includes(c.smeCountryId) ? "Y" : "N",
              }))}
              onToggle={handleCountryToggle}
              loading={false}
              label="Where do you do business?"
              className="mt-6"
            />
          </div>
        </>
      )}
    </div>
  );
};

export default InviteStep;